import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { getEnvoiConfig } from '../lib/envoiConfig';
import { fetchTwelveLabsVideo, extractApiErrorMessage } from '../lib/twelvelabs';

function formatDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
        : `${m}:${String(sec).padStart(2, '0')}`;
}

function formatBytes(bytes) {
    if (!bytes) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let val = bytes;
    let i = 0;
    while (val >= 1024 && i < units.length - 1) {
        val /= 1024;
        i++;
    }
    return `${val.toFixed(val < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

// Converts a clock-style timestamp ("1:02:03", "02:03") into seconds.
function parseClockToSeconds(ts) {
    if (typeof ts === 'number') return ts;
    if (typeof ts !== 'string') return 0;
    return ts.split(':').map(Number).reduce((acc, v) => acc * 60 + (Number.isFinite(v) ? v : 0), 0);
}

// Parses lines like "00:00-00:05: A monk and a boy walk towards a temple."
// (the older free-text format some cached chapter-markers results still use)
// into { start, end, text } entries with times in seconds.
function parseChapterMarkers(text) {
    if (typeof text !== 'string') return [];
    const re = /^(\d{1,2}(?::\d{2}){1,2})\s*-\s*(\d{1,2}(?::\d{2}){1,2})\s*:\s*(.+)$/;
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => line.match(re))
        .filter(Boolean)
        .map((m) => ({ start: parseClockToSeconds(m[1]), end: parseClockToSeconds(m[2]), text: m[3].trim() }));
}

// Normalizes chapter-marker results into a common { start, end, text } shape
// (times in seconds). Handles the current structured API/cache format —
// [{ start: "00:00", end: "00:04", description: "..." }], optionally wrapped
// as { "chapter-markers": [...] } — whether already parsed, still a
// JSON-encoded string, or (for older cached results) the free-text format.
function normalizeChapterEntries(value) {
    if (Array.isArray(value)) {
        return value.map((e) => ({
            start: parseClockToSeconds(e.start),
            end: parseClockToSeconds(e.end),
            text: e.description ?? e.text ?? '',
        }));
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
            try {
                const parsed = JSON.parse(trimmed);
                const raw = Array.isArray(parsed) ? parsed : parsed?.['chapter-markers'];
                if (Array.isArray(raw)) return normalizeChapterEntries(raw);
            } catch {
                // not JSON — fall through to free-text parsing
            }
        }
        return parseChapterMarkers(value);
    }
    return [];
}

// Normalizes segmentsKey general results (e.g. Detect Criminal Activity's
// "incidents") into the { start, end, ...fields } shape SegmentTimelineBar /
// ActiveSegmentCard expect — the raw entries use start_time_sec/end_time_sec
// and structured persons/vehicles arrays instead.
function normalizeIncidentSegments(raw) {
    if (!Array.isArray(raw)) return null;
    return raw.map((entry) => {
        const { start_time_sec, end_time_sec, persons, vehicles, ...rest } = entry;
        const flat = { start: start_time_sec, end: end_time_sec ?? start_time_sec, ...rest };
        if (Array.isArray(persons) && persons.length) {
            flat.persons = persons.map((p) => `${p.role}: ${p.description}`).join('; ');
        }
        if (Array.isArray(vehicles) && vehicles.length) {
            flat.vehicles = vehicles.map((v) => v.description).join('; ');
        }
        return flat;
    });
}

function formatDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
}

function parseMaybeJson(value) {
    if (value == null || value === '') return null;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

function flattenValue(value) {
    if (value == null) return null;
    if (Array.isArray(value)) return value.join(', ') || null;
    if (typeof value === 'object') return value.name || null;
    return value;
}

function SegmentTimelineBar({ segments, currentTime, duration, onSeek }) {
    const total = duration || segments[segments.length - 1]?.end || 1;
    const activeIndex = segments.findIndex((s) => currentTime >= s.start && currentTime < s.end);
    const tickCount = 5;
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => Math.round((total / tickCount) * i));

    const handleClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        onSeek(ratio * total);
    };

    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                {ticks.map((t, i) => (
                    <span key={i} style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                        {formatDuration(t)}
                    </span>
                ))}
            </div>
            <div onClick={handleClick} style={{ position: 'relative', height: 30, cursor: 'pointer', background: 'rgba(255,255,255,0.05)', borderRadius: 5 }}>
                {segments.map((s, idx) => {
                    const isActive = idx === activeIndex;
                    const leftPct = Math.min(100, Math.max(0, (s.start / total) * 100));
                    const widthPct = Math.max(0.5, ((s.end - s.start) / total) * 100);
                    const playheadPct = isActive
                        ? Math.min(100, Math.max(0, ((currentTime - s.start) / Math.max(0.001, s.end - s.start)) * 100))
                        : 0;
                    return (
                        <div
                            key={idx}
                            title={s.segment_title}
                            style={{
                                position: 'absolute', top: 0, bottom: 0,
                                left: `${leftPct}%`, width: `${widthPct}%`, boxSizing: 'border-box',
                                borderRadius: 4, borderRight: '2px solid #16162a',
                                background: isActive ? '#f59e0b' : 'rgba(250,204,21,0.18)',
                                border: '1px solid rgba(250,204,21,0.35)',
                                transition: 'background 0.15s',
                            }}
                        >
                            {isActive && (
                                <div style={{
                                    position: 'absolute', top: -2, bottom: -2, left: `${playheadPct}%`,
                                    width: 2, background: '#dc2626', transform: 'translateX(-1px)',
                                }} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Builds the "Label: value" pairs shown in the on-video overlay for the active
// segment, per segment-definition id — result field names differ per type.
function buildSegmentOverlayText(definitionId, segment) {
    if (!segment) return definitionId;
    const pair = (label, value) => (value ? `${label}: ${value}` : null);
    let parts;
    switch (definitionId) {
        case 'editorial_narratives':
            parts = [pair('Segment Title', segment.segment_title)];
            break;
        case 'content_structure':
        case 'shot_changes':
            parts = [pair('Description', segment.description)];
            break;
        case 'chapters':
            parts = [pair('Description', segment.text)];
            break;
        case 'person_of_focus':
            parts = [pair('Person', segment.person), pair('Screen Position', segment.screen_position)];
            break;
        case 'topic_segments':
            parts = [pair('Topic', segment.topic)];
            break;
        case 'speakers':
            parts = [pair('Speaker', segment.speaker_label), pair('Summary', segment.transcript_summary)];
            break;
        case 'plays':
            parts = [
                pair('Key Players', segment.key_players),
                pair('Play Type', segment.play_type),
                segment.scoring_play ? 'Scoring Play' : null,
            ];
            break;
        case 'incidents':
            parts = [pair('Incident', segment.incident_type), pair('Summary', segment.summary)];
            break;
        default:
            parts = [];
    }
    return parts.filter(Boolean).join(' · ') || definitionId;
}

// Slim chapter-marker style seekbar rendered over the video itself (replaces
// Bitmovin's native seekbar) — deliberately much thinner than the sidebar's
// SegmentTimelineBar, with legible pill-style labels instead of a tick row.
function PlayerSegmentSeekbar({ segments, currentTime, duration, onSeek, definitionId }) {
    const total = duration || segments[segments.length - 1]?.end || 1;
    const activeIndex = segments.findIndex((s) => currentTime >= s.start && currentTime < s.end);
    const activeSeg = activeIndex >= 0 ? segments[activeIndex] : null;
    const playheadPct = Math.min(100, Math.max(0, (currentTime / total) * 100));
    const label = buildSegmentOverlayText(definitionId, activeSeg);

    const handleClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        onSeek(ratio * total);
    };

    const pillStyle = {
        fontSize: 11, fontWeight: 600, color: '#fff', fontFamily: 'ui-monospace, monospace',
        background: 'rgba(0,0,0,0.65)', borderRadius: 5, padding: '2px 7px', backdropFilter: 'blur(2px)',
    };

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={pillStyle}>
                    {label}
                    {activeSeg && ` · ${formatDuration(activeSeg.start)}–${formatDuration(activeSeg.end)}`}
                </span>
                <span style={pillStyle}>
                    {formatDuration(currentTime)} / {formatDuration(total)}
                </span>
            </div>

            <div onClick={handleClick} style={{ position: 'relative', height: 16, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <div style={{ position: 'relative', width: '100%', height: 8, borderRadius: 3, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
                    {segments.map((s, idx) => {
                        const leftPct = Math.min(100, Math.max(0, (s.start / total) * 100));
                        const widthPct = Math.max(0.3, ((s.end - s.start) / total) * 100);
                        return (
                            <div
                                key={idx}
                                title={s.segment_title || s.text}
                                style={{
                                    position: 'absolute', top: 0, bottom: 0,
                                    left: `${leftPct}%`, width: `${widthPct}%`,
                                    background: idx === activeIndex ? '#f59e0b' : 'rgba(245,158,11,0.35)',
                                    borderRight: '1px solid rgba(0,0,0,0.5)',
                                    transition: 'background 0.15s',
                                }}
                            />
                        );
                    })}
                </div>
                <div style={{
                    position: 'absolute', top: '50%', left: `${playheadPct}%`,
                    width: 11, height: 11, borderRadius: '50%', background: '#fff',
                    border: '2px solid #f59e0b', transform: 'translate(-50%, -50%)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.7)', pointerEvents: 'none',
                }} />
            </div>
        </div>
    );
}

function ActiveSegmentCard({ definitionId, segment, index, total, onSeek }) {
    if (!segment) {
        return <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Play the video to see the active segment here.</p>;
    }
    const { start, end, ...metadata } = segment;
    const fields = Object.entries(metadata).sort(([a], [b]) => a.localeCompare(b));

    return (
        <div style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 14, background: 'rgba(255,255,255,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.7)' }}>
                    {definitionId}
                </span>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onClick={() => onSeek(start)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 20, padding: '3px 10px', color: '#fff', cursor: 'pointer',
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>play_arrow</span>
                    {formatDuration(start)}–{formatDuration(end)}
                </button>
                <span style={{
                    fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 20, padding: '3px 10px',
                }}>
                    {index + 1}/{total}
                </span>
            </div>

            {fields.map(([key, value]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>{key}</div>
                    <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.5 }}>{String(value)}</div>
                </div>
            ))}
        </div>
    );
}

function SegmentTypeDetail({ seg, task, onRun, onSeek, currentTime, videoDuration }) {
    const { description, definition, general } = seg;
    const status = task?.status;
    const segments = task?.segments;
    const summary = task?.summary;
    const overallSummary = task?.overallSummary;
    const chapterEntries = general?.isChapters ? normalizeChapterEntries(summary) : [];

    return (
        <div>
            <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.6)' }}>
                {description}
            </p>

            {!definition && !general && (
                <div style={{
                    padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)',
                    border: '1px dashed rgba(255,255,255,0.12)', fontSize: 12, color: 'rgba(255,255,255,0.4)',
                    textAlign: 'center',
                }}>
                    This feature isn't wired up yet — give me its API details (curl or docs) and I'll enable it.
                </div>
            )}

            {(definition || general) && (
                <>
                    {(definition || general?.segmentsKey) && status === 'ready' && Array.isArray(segments) && segments.length > 0 && (() => {
                        const timelineId = definition?.id || general?.segmentsKey;
                        const activeIndex = segments.findIndex((s) => currentTime >= s.start && currentTime < s.end);
                        const activeSegment = activeIndex >= 0 ? segments[activeIndex] : segments[0];
                        return (
                            <div>
                                <SegmentTimelineBar
                                    segments={segments}
                                    currentTime={currentTime}
                                    duration={videoDuration}
                                    onSeek={onSeek}
                                />
                                <div style={{
                                    fontSize: 11, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                                    color: 'rgba(255,255,255,0.5)', marginBottom: 14,
                                }}>
                                    {timelineId}
                                </div>
                                <ActiveSegmentCard
                                    definitionId={timelineId}
                                    segment={activeSegment}
                                    index={Math.max(0, activeIndex)}
                                    total={segments.length}
                                    onSeek={onSeek}
                                />
                            </div>
                        );
                    })()}

                    {general?.segmentsKey && status === 'ready' && overallSummary && (
                        <div style={{
                            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 14,
                            background: 'rgba(255,255,255,0.03)',
                            marginTop: (Array.isArray(segments) && segments.length > 0) ? 14 : 0,
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>
                                Overall Summary
                            </div>
                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: '#fff' }}>{overallSummary}</p>
                        </div>
                    )}

                    {general?.segmentsKey && status === 'ready' && !overallSummary && !(Array.isArray(segments) && segments.length > 0) && (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>No incidents were detected in this video.</p>
                    )}

                    {general && status === 'ready' && summary && typeof summary === 'string' && chapterEntries.length === 0 && (
                        <div style={{
                            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 14,
                            background: 'rgba(255,255,255,0.03)',
                        }}>
                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: '#fff', whiteSpace: 'pre-wrap' }}>
                                {summary}
                            </p>
                        </div>
                    )}

                    {general?.isChapters && status === 'ready' && chapterEntries.length > 0 && (
                        <div>
                            {chapterEntries.map((c, idx) => {
                                const isActive = currentTime >= c.start && currentTime < c.end;
                                return (
                                    <div
                                        key={idx}
                                        onClick={() => onSeek(c.start)}
                                        style={{
                                            padding: '8px 10px', marginBottom: 4, borderRadius: 8, cursor: 'pointer',
                                            background: isActive ? 'rgba(124,58,237,0.22)' : 'transparent',
                                            border: isActive ? '1px solid rgba(124,58,237,0.5)' : '1px solid transparent',
                                            transition: 'background 0.15s, border-color 0.15s',
                                        }}
                                    >
                                        <span style={{
                                            fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                                            color: isActive ? '#c4b5fd' : '#a78bfa',
                                        }}>
                                            {formatDuration(c.start)}-{formatDuration(c.end)}
                                        </span>
                                        <span style={{ fontSize: 13, color: isActive ? '#fff' : 'rgba(255,255,255,0.75)', marginLeft: 6 }}>
                                            : {c.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {general && status === 'ready' && summary && typeof summary === 'object' && !Array.isArray(summary) && (
                        <div style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 14, background: 'rgba(255,255,255,0.03)' }}>
                            {Array.isArray(summary.hashtags) && summary.hashtags.length > 0 && (
                                <div style={{ marginBottom: Array.isArray(summary.topics) && summary.topics.length > 0 ? 18 : 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 10 }}>
                                        Hashtags
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {summary.hashtags.map((tag, i) => (
                                            <span key={i} style={{
                                                fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.05)',
                                                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '6px 14px',
                                            }}>
                                                {tag.startsWith('#') ? tag : `#${tag}`}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {Array.isArray(summary.topics) && summary.topics.length > 0 && (
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 10 }}>
                                        Topics
                                    </div>
                                    <div>
                                        {summary.topics.map((topic, i) => (
                                            <p key={i} style={{ margin: '0 0 6px', fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                                                {topic}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}



                    {status === 'ready' && general && !general.segmentsKey && !summary && (
                        <div>
                            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                                Analysis finished but no text was returned — raw result:
                            </p>
                            <pre style={{
                                fontSize: 11, color: 'rgba(255,255,255,0.6)', background: 'rgba(0,0,0,0.3)',
                                padding: 10, borderRadius: 8, overflow: 'auto', maxHeight: 240,
                            }}>
                                {JSON.stringify(task.raw, null, 2)}
                            </pre>
                        </div>
                    )}

                    {status === 'running' && (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 14 }}>Starting analysis…</p>
                    )}
                    {status === 'polling' && (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 14 }}>Analyzing video — this can take a minute, switch tabs and back to check.</p>
                    )}
                    {status === 'error' && (
                        <p style={{ fontSize: 12, color: '#f87171', marginTop: 14 }}>{task.error || 'Something went wrong.'}</p>
                    )}
                    {status === 'ready' && task?.fromCache && (
                        <p style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 14 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>check_circle</span>
                            Loaded from saved results
                        </p>
                    )}

                    <button
                        type="button"
                        onClick={onRun}
                        disabled={status === 'running' || status === 'polling'}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8,
                            border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
                            cursor: (status === 'running' || status === 'polling') ? 'not-allowed' : 'pointer',
                            opacity: (status === 'running' || status === 'polling') ? 0.6 : 1, marginTop: 16,
                        }}
                    >
                        {(status === 'running' || status === 'polling') && <span className="ai-spinner ai-spinner--sm" />}
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>play_arrow</span>
                        {status === 'ready' ? 'Run again' : 'Run analysis'}
                    </button>
                </>
            )}
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div style={{ marginBottom: 7 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{label} : </span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{value ?? 'N/A'}</span>
        </div>
    );
}

const SEGMENT_TYPES = [
    {
        key: 'summarize-video',
        icon: 'summarize',
        title: 'Summary',
        description: 'Generate a concise summary of this video.',
        general: {
            prompt: 'Summarize this video',
            temperature: 0.2,
            maxTokens: 4096,
        },
    },
    {
        key: 'chapter-markers',
        icon: 'toc',
        title: 'Chapter Markers',
        description: 'Create chapter markers for easy navigation through this video.',
        general: {
            prompt: 'Break down the video by main event and timestamp',
            temperature: 0.2,
            maxTokens: 4096,
            isChapters: true,
        },
    },
    {
        key: 'editorial_narratives',
        icon: 'auto_stories',
        title: 'Editorial Narratives',
        description: 'Segment this video into editorial segments. Each segment should focus on a central topic, theme, or subject.',
        definition: {
            id: 'editorial_narratives',
            description: 'Segment this video into editorial segments. Each segment should focus on a central topic, theme, or subject.',
            fields: [
                { name: 'segment_title', type: 'string', description: 'Title of the editorial segment' },
                { name: 'description', type: 'string', description: 'Brief description of the narrative (200 chars max)' },
                { name: 'editorial_subjects', type: 'string', description: 'Topics and themes in this segment' },
                { name: 'visual_subjects', type: 'string', description: 'Key visual elements (people, places, objects)' },
                { name: 'names', type: 'string', description: 'Identifiable people mentioned or visible' },
                { name: 'confidence', type: 'string', enum: ['HIGH', 'MEDIUM', 'LOW'] },
            ],
        },
    },
    {
        key: 'content_structure',
        icon: 'account_tree',
        title: 'Content Structure',
        description: 'Segment the video into structural sections (intro, main content, credits, etc.).',
        definition: {
            id: 'content_structure',
            description: 'Segment the video into structural sections (intro, main content, credits, etc.).',
            fields: [
                { name: 'section_type', type: 'string', enum: ['intro', 'opening_credits', 'recap', 'main_content', 'commercial_break', 'transition', 'closing', 'end_credits', 'post_credits'] },
                { name: 'description', type: 'string', description: 'Brief description of the section' },
                { name: 'contains_graphics', type: 'boolean', description: 'Whether the section contains title cards, lower thirds, or other graphics' },
            ],
        },
    },
    {
        key: 'shot_changes',
        icon: 'split_scene',
        title: 'Shot Changes',
        description: 'Segment whenever there is a hard cut or significant change in camera angle.',
        definition: {
            id: 'shot_changes',
            description: 'Segment whenever there is a hard cut or significant change in camera angle.',
            fields: [
                { name: 'angle_type', type: 'string', enum: ['wide', 'medium', 'close_up', 'extreme_close_up', 'overhead', 'aerial'] },
                { name: 'shot_type', type: 'string', enum: ['clean', 'dirty_single', 'over_the_shoulder', 'point_of_view', 'insert', 'master'] },
                { name: 'description', type: 'string', description: 'Brief description of the shot content' },
            ],
        },
    },
    {
        key: 'hashtags-topics',
        icon: 'tag',
        title: 'Hashtags and Topics',
        description: 'Generate relevant hashtags and topics for this video.',
        general: {
            prompt: 'Summarize this video',
            temperature: 0.2,
            maxTokens: 4096,
            jsonSchema: {
                type: 'object',
                properties: {
                    topics: { type: 'array', items: { type: 'string' } },
                    hashtags: { type: 'array', items: { type: 'string' } },
                },
            },
        },
    },
    {
        key: 'person_of_focus',
        icon: 'ar_on_you',
        title: 'Person of Focus',
        description: 'Create a new segment each time the primary person of focus changes.',
        definition: {
            id: 'person_of_focus',
            description: 'Create a new segment each time the primary person of focus changes.',
            fields: [
                { name: 'person', type: 'string', description: 'Description of the person (appearance, role)' },
                { name: 'activity', type: 'string', description: 'What the person is doing' },
                { name: 'screen_position', type: 'string', enum: ['center', 'left', 'right', 'background'] },
                { name: 'speaking', type: 'boolean', description: 'Whether the person is speaking' },
            ],
        },
    },
    {
        key: 'topic_segments',
        icon: 'segment',
        title: 'Top Segments',
        description: 'Segment whenever the primary topic or subject matter changes significantly.',
        definition: {
            id: 'topic_segments',
            description: 'Segment whenever the primary topic or subject matter changes significantly.',
            fields: [
                { name: 'topic', type: 'string', description: 'The main topic of this segment' },
                { name: 'subtopics', type: 'string', description: 'Related subtopics discussed' },
                { name: 'key_points', type: 'string', description: 'Key points or takeaways from this segment' },
                { name: 'transition_type', type: 'string', enum: ['hard_cut', 'gradual', 'host_introduction', 'natural_flow'] },
            ],
        },
    },
    {
        key: 'speakers',
        icon: 'frame_person_mic',
        title: 'Speaker Diarization',
        description: 'Segment whenever the primary speaker changes. Create a new segment for each speaker turn.',
        definition: {
            id: 'speakers',
            description: 'Segment whenever the primary speaker changes. Create a new segment for each speaker turn.',
            fields: [
                { name: 'speaker_label', type: 'string', description: "Name or identifying label for the speaker (e.g. 'Host', 'Guest 1', 'Reporter'). Use short descriptive text if no name is present." },
                { name: 'transcript_summary', type: 'string', description: 'Brief summary of what was said' },
                { name: 'speaking_context', type: 'string', enum: ['interview', 'narration', 'dialogue', 'presentation', 'commentary'] },
            ],
        },
    },
    {
        key: 'moderate-content',
        icon: 'add_moderator',
        title: 'Moderate Content',
        description: 'Detect content in this video that may need moderation review.',
        general: {
            prompt: 'Which audience is the video suitable for, and why?',
            temperature: 0.2,
            maxTokens: 4096,
        },
    },
    {
        key: 'detect-criminal-activity',
        icon: 'detection_and_zone',
        title: 'Detect Criminal Activity',
        description: 'Detect potential criminal activity within this video.',
        general: {
            prompt: 'Look for illegal criminal, civil, or traffic crimes in this video',
            temperature: 0.2,
            maxTokens: 4096,
            segmentsKey: 'incidents',
            jsonSchema: {
                type: 'object',
                properties: {
                    incidents: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                incident_type: {
                                    type: 'string',
                                    enum: ['theft', 'assault', 'vandalism', 'trespassing', 'accident', 'suspicious_activity', 'other'],
                                },
                                start_time_sec: { type: 'number' },
                                end_time_sec: { type: 'number' },
                                summary: { type: 'string' },
                                persons: {
                                    type: 'array',
                                    items: {
                                        type: 'object',
                                        properties: {
                                            role: { type: 'string', enum: ['suspect', 'victim', 'bystander', 'unknown'] },
                                            description: { type: 'string' },
                                        },
                                        required: ['role', 'description'],
                                    },
                                },
                                vehicles: {
                                    type: 'array',
                                    items: {
                                        type: 'object',
                                        properties: {
                                            description: { type: 'string' },
                                            first_seen_time_sec: { type: 'number' },
                                        },
                                    },
                                },
                            },
                            required: ['incident_type', 'start_time_sec', 'summary'],
                        },
                    },
                    overall_summary: { type: 'string' },
                },
                required: ['incidents'],
            },
        },
    },
    {
        key: 'gameplay',
        icon: 'sports_soccer',
        title: 'Game Play',
        assetType: 'Sports',
        description: 'Segment every play or key moment in the game, ignoring replays and non-gameplay.',
        definition: {
            id: 'plays',
            description: 'Segment every play or key moment in the game, ignoring replays and non-gameplay.',
            fields: [
                { name: 'play_type', type: 'string', description: 'Type of play or moment' },
                { name: 'scoring_play', type: 'boolean', description: 'Did this result in points scored?' },
                { name: 'key_players', type: 'string', description: 'Players involved in this play' },
                { name: 'excitement_level', type: 'string', enum: ['HIGH', 'MEDIUM', 'LOW'] },
                { name: 'description', type: 'string', description: 'Brief description of the play' },
            ],
        },
    },
];

const RAIL_ITEMS = [
    { key: 'info', icon: 'movie_info', label: 'Metadata' },
    { key: 'visual-intelligence', icon: 'speech_to_text', label: 'Transcription' },
    ...SEGMENT_TYPES.map((seg) => ({ key: seg.key, icon: seg.icon, label: seg.title })),
    { key: 'save-as', icon: 'file_save', label: 'Save As' },
];

const SAVE_AS_TARGETS = [
    {
        key: 'davinci-resolve-xml',
        label: 'Davinci Resolve',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/4/4d/DaVinci_Resolve_Studio.png',
        pathField: 'resolveXml',
        filePrefix: 'Davinci',
    },
    {
        key: 'premiere-pro-xml',
        label: 'Adobe Premiere Pro',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/34/Adobe_Premiere_Pro_CC_2026_icon.svg/1280px-Adobe_Premiere_Pro_CC_2026_icon.svg.png',
        pathField: 'premiereXml',
        filePrefix: 'Premiere Pro',
    },
];

// The NLE exports are built from these three prior analyses — Save As can't
// prepare Resolve/Premiere XML until all three have been run for this asset.
const SAVE_AS_REQUIRED_SEGMENTS = [
    { key: 'chapter-markers', label: 'Chapter Markers' },
    { key: 'editorial_narratives', label: 'Editorial Narratives' },
    { key: 'topic_segments', label: 'Topic Segments' },
];

function hasSegmentData(value) {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return false;
}

export default function TwelveLabsPlayerPage() {
    const history = useHistory();
    const { lambdaUrl, appname, environmentVariable, playerKey, analyticsKey } = getEnvoiConfig();
    const playerContainerRef = useRef(null);
    const playerRef = useRef(null);
    const transcriptRefs = useRef({});
    const mountedRef = useRef(true);

    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [analysisTasks, setAnalysisTasks] = useState({});
    const [activeTab, setActiveTab] = useState('info');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [assetRecord, setAssetRecord] = useState(null);
    const [saveAsMessage, setSaveAsMessage] = useState('');
    const [saveAsPreparing, setSaveAsPreparing] = useState(false);
    const [saveAsAttempted, setSaveAsAttempted] = useState(false);
    const [analyzeAllStatus, setAnalyzeAllStatus] = useState(null); // null | 'running' | 'error'

    const parts = window.location.pathname.split('/').filter(Boolean);
    const assetId = parts.pop();
    const videoId = parts.pop();
    const indexId = parts.pop();

    // Re-run whenever a tab is opened (see openTab) so a "Run analysis" kicked
    // off server-side shows up as soon as its result lands on the asset record —
    // there's no push/poll from the analyzeEvents API, so switching tabs is
    // what re-checks for a finished result.
    const fetchAssetRecord = () => {
        if (!assetId) return;
        const token = sessionStorage.getItem('token');
        axios
            .post(
                `${lambdaUrl}assetDetails?appname=${appname}&env=${environmentVariable}&assetid=${encodeURIComponent(assetId)}&appType=relay`,
                {},
                { headers: { 'Content-Type': 'application/json' } }
            )
            .then((res) => setAssetRecord(res?.data?.result || res?.data || {}))
            .catch((err) => console.error('❌ TwelveLabsPlayerPage: assetDetails fetch failed:', err));
    };

    useEffect(() => {
        fetchAssetRecord();
    }, [assetId]);

    useEffect(() => {
        if (!indexId || !videoId || !assetId) {
            setError('Missing indexId/videoId/assetid in the URL.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');

        fetchTwelveLabsVideo(indexId, videoId)
            .then((data) => 
                console.log('ℹ️ TwelveLabsPlayerPage: video fetch succeeded:', data) ||
                setVideo(data)
        
        )
            .catch((err) => {
                console.error('❌ TwelveLabsPlayerPage: video fetch failed:', err);
                setError('Could not load this video.');
            })
            .finally(() => setLoading(false));
    }, [indexId, videoId, assetId]);

    useEffect(() => {
        const hlsUrl = video?.hls?.video_url;
        if (!hlsUrl || !playerContainerRef.current) return;

        if (!window.bitmovin?.player) {
            console.error('❌ TwelveLabsPlayerPage: Bitmovin SDK not loaded');
            return;
        }
console.log('ℹ️ TwelveLabsPlayerPage: initializing Bitmovin player with HLS URL:', hlsUrl);
        const Player = window.bitmovin.player.Player;
        const PlayerEvent = window.bitmovin.player.PlayerEvent;
        const player = new Player(playerContainerRef.current, {
            key: playerKey,
            analytics: {
                key: analyticsKey,
            },
            playback: { autoplay: true, muted: false },
            ui: { logo: { enabled: false }, watermark: { enabled: false } },
            adaptation: {
                desktop: {
                    limitToPlayerSize: false,
                },
            },
        });
        playerRef.current = player;

        // React 18 StrictMode double-invokes effects in dev (mount → cleanup →
        // mount), which destroys this first player while .load() is still in
        // flight and rejects with a "cancelled" error. That's expected noise
        // from the throwaway first instance, not a real failure — the second
        // (surviving) instance's load() resolves normally — so don't log it
        // once we've intentionally torn this instance down.
        let destroyed = false;

        player
            .load({ hls: hlsUrl, title: video?.user_metadata?.assetName || video?.system_metadata?.filename || 'Video' })
            .then(() => {
                player.on(PlayerEvent.TimeChanged, (e) => setCurrentTime(e.time));
                player.on(PlayerEvent.Error, (event) => console.error('❌ Bitmovin player error:', event.code, event.message));
            })
            .catch((err) => {
                if (!destroyed) console.error('❌ TwelveLabsPlayerPage: player load failed:', err);
            });

        return () => {
            destroyed = true;
            try {
                player.destroy();
            } catch (e) {
                // ignore
            }
            playerRef.current = null;
        };
    }, [video, loading, error]);

    const activeTranscriptIndex = (() => {
        const transcript = video?.transcription || video?.transcript || [];
        return transcript.findIndex((line) => currentTime >= (line.start ?? 0) && currentTime < (line.end ?? line.start ?? 0));
    })();

    useEffect(() => {
        if (activeTab !== 'visual-intelligence' || activeTranscriptIndex < 0) return;
        transcriptRefs.current[activeTranscriptIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [activeTranscriptIndex, activeTab]);

    const handleBack = () => {
        if (playerRef.current) {
            try {
                playerRef.current.pause();
                playerRef.current.destroy();
            } catch (e) {
                // ignore
            }
            playerRef.current = null;
        }
        const previousRoute = localStorage.getItem('previousDashboardRoute') || '/ai-search';
        localStorage.removeItem('previousDashboardRoute');
        history.push(previousRoute);
    };

    const openTab = (key) => {
        setActiveTab(key);
        setSidebarCollapsed(false);
        fetchAssetRecord();
    };

    const seekTo = (seconds) => {
        playerRef.current?.seek(seconds);
    };

    // Downloads a "Save As" export (Resolve/Premiere XML) — the asset record
    // holds a relative path (assetRecord.resolveXml / .premiereXml) that needs
    // sourceCloudFront prepended, same as the image URLs elsewhere on this page.
    // The file is on a different origin (CloudFront), so the anchor `download`
    // attribute alone is ignored by the browser and it just opens the file
    // instead — fetching it as a blob and downloading that (same-origin) URL
    // is what actually forces a save-to-disk.
    const handleSaveAsDownload = async (target) => {
        const relPath = assetRecord?.[target.pathField];
        const config = (window.site && window.site.config) || (window.app && window.app.defaultSiteSettings && window.app.defaultSiteSettings.config) || {};
        const sourceCloudFront = config.sourceCloudFront || '';
        if (!relPath) {
            setSaveAsMessage(`${target.label} isn't available for this asset yet.`);
            return;
        }
        const fullUrl = sourceCloudFront ? `${sourceCloudFront}/${relPath}`.replace(/ /g, '%20') : relPath;
        const ext = relPath.includes('.') ? relPath.slice(relPath.lastIndexOf('.')) : '';
        const assetname = (assetRecord?.assetname || video?.user_metadata?.assetName || 'export').replace(/[\\/:*?"<>|]/g, '_').trim();
        const fileName = `${target.filePrefix}_${assetname}${ext}`;
        setSaveAsMessage('');
        try {
            const response = await axios.get(fullUrl, { responseType: 'blob' });
            const blobUrl = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('❌ Save As download failed:', err);
            setSaveAsMessage(`Could not download ${target.label}.`);
        }
    };

    // Generates the Resolve/Premiere XML exports for this asset (from its
    // already-run chapter/editorial/topic analyses) when neither is present
    // yet on the asset record — shown as a loader until the API responds.
    const prepareSaveAsExports = async () => {
        setSaveAsPreparing(true);
        setSaveAsMessage('');
        try {
            const token = sessionStorage.getItem('token');
            const body = {
                filePath: assetRecord?.video?.mainVideo?.filePath || assetRecord?.filepath || '',
                assetId: assetRecord?.assetid || assetId,
                assetname: assetRecord?.assetname || video?.user_metadata?.assetName || '',
                chaptersJSON: assetRecord?.["chapter-markers"] || '',
                editorialJSON: assetRecord?.editorial_narratives || '',
                topicJSON: assetRecord?.topic_segments || '',
            };
            const resp = await axios.post(
                `${lambdaUrl}downloadDavinci?appname=${appname}&env=${environmentVariable}`,
                body,
                { headers: { 'Content-Type': 'application/json' } }
            );
            const result = resp?.data?.result || resp?.data || {};
            setAssetRecord((prev) => ({
                ...(prev || {}),
                resolveXml: result?.resolveXml || prev?.resolveXml,
                premiereXml: result?.premiereXml || prev?.premiereXml,
            }));
        } catch (err) {
            console.error('❌ downloadDavinci failed:', err);
            setSaveAsMessage('Could not prepare NLE exports for this asset.');
        } finally {
            setSaveAsPreparing(false);
            setSaveAsAttempted(true);
        }
    };

    // Fires once when the Save As tab is opened, only if the exports aren't
    // already on the asset record and their prerequisite analyses have run.
    useEffect(() => {
        if (activeTab !== 'save-as' || !assetRecord || saveAsPreparing || saveAsAttempted) return;
        if (assetRecord?.resolveXml && assetRecord?.premiereXml) {
            setSaveAsAttempted(true);
            return;
        }
        const missingPrereqs = SAVE_AS_REQUIRED_SEGMENTS.filter(
            (s) => !hasSegmentData(assetRecord?.[s.key])
        );
        if (missingPrereqs.length > 0) {
            setSaveAsAttempted(true);
            return;
        }
        prepareSaveAsExports();
    }, [activeTab, assetRecord, saveAsPreparing, saveAsAttempted]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const setTaskState = (segKey, patch) => {
        setAnalysisTasks((prev) => ({ ...prev, [segKey]: { ...prev[segKey], ...patch } }));
    };

    // Some "general" results were persisted before topics/hashtags parsing was
    // added, as a pretty-printed JSON string. Recover the structured object from
    // those so old cached results still render as chips instead of raw text.
    const normalizeGeneralCache = (value) => {
        if (typeof value !== 'string') return value;
        const trimmed = value.trim();
        if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
        try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === 'object' && (Array.isArray(parsed.topics) || Array.isArray(parsed.hashtags))) {
                return parsed;
            }
        } catch {
            // not JSON — leave as plain text
        }
        return value;
    };

    // Persist the result (segment array or general text) onto the Envoi asset —
    // sends only the single changed field (e.g. "segments.speakers") rather than
    // the whole segments object, so unrelated tabs' saved results aren't re-sent
    // on every update; keyed by the TwelveLabs segment_definitions id, or our
    // own tab key for general mode.
    const persistSegments = async (storageId, value) => {
        if (!assetId || !value) return;
        // Trim stray leading/trailing newlines/whitespace the model tends to add
        // (e.g. "\n\nThe video captures...") — internal line breaks (like the
        // separators between Chapter Markers lines) are left untouched.
        const cleaned = typeof value === 'string' ? value.trim() : value;
        try {
            const token = sessionStorage.getItem('token');
            await axios.post(
                `${lambdaUrl}updateAsset?appname=${appname}&env=${environmentVariable}&assetid=${encodeURIComponent(assetId)}`,
                { assetid: assetId, [`segments.${storageId}`]: cleaned },
                { headers: { 'Content-Type': 'application/json' } }
            );
            setAssetRecord((prev) => ({ ...(prev || {}), segments: { ...(prev?.segments || {}), [storageId]: cleaned } }));
        } catch (err) {
            console.error('❌ TwelveLabsPlayerPage: updateAsset (segments) failed:', err);
        }
    };

    // Kicks off analysis server-side via the content-screening-api — it runs
    // the TwelveLabs call and persists the result onto the asset's
    // segments.<eventType> field itself, so there's nothing to poll here:
    // the result shows up whenever assetRecord is next re-fetched (on tab
    // switch, via openTab/fetchAssetRecord) and the cache-loading effect
    // below picks it up.
    const runSegmentAnalysis = async (seg) => {
        if ((!seg.definition && !seg.general) || !videoId) return;
        setTaskState(seg.key, { status: 'running', segments: null, summary: null, error: null, fromCache: false });
        try {
            const token = sessionStorage.getItem('token');
            const filePath = assetRecord?.video?.mainVideo?.filePath || assetRecord?.filepath || '';
            await axios.post(
                `${lambdaUrl}analyzeEvents?appname=${appname}&env=${environmentVariable}`,
                { filePath, assetId, videoId, eventType: seg.key },
                { headers: { 'Content-Type': 'application/json' } }
            );
            setTaskState(seg.key, { status: 'polling' });
        } catch (err) {
            console.error('❌ analyzeEvents failed:', err);
            setTaskState(seg.key, { status: 'error', error: extractApiErrorMessage(err, 'Could not start the analysis task.') });
        }
    };

    // Same analyzeEvents call as runSegmentAnalysis, just without eventType —
    // the backend runs every analysis type for this asset instead of one.
    const runAllAnalysis = async () => {
        if (!videoId || analyzeAllStatus === 'running') return;
        setAnalyzeAllStatus('running');
        try {
            const token = sessionStorage.getItem('token');
            const filePath = assetRecord?.video?.mainVideo?.filePath || assetRecord?.filepath || '';
            await axios.post(
                `${lambdaUrl}analyzeEvents?appname=${appname}&env=${environmentVariable}`,
                { filePath, assetId, videoId },
                { headers: { 'Content-Type': 'application/json' } }
            );
            setAnalyzeAllStatus(null);
        } catch (err) {
            console.error('❌ analyzeEvents (all) failed:', err);
            setAnalyzeAllStatus('error');
        }
    };

    // On opening a tab (and every re-fetch of assetRecord that follows — see
    // openTab/fetchAssetRecord): use the saved result from the asset record if
    // one is there. Only skips once a tab already has a 'ready' result — a
    // 'running'/'polling' tab (kicked off via runSegmentAnalysis) keeps
    // re-checking here on every assetRecord refresh until the result appears.
    useEffect(() => {
        const seg = SEGMENT_TYPES.find((s) => s.key === activeTab);
        if ((!seg?.definition && !seg?.general) || !assetRecord || analysisTasks[seg.key]?.status === 'ready') return;

        // segment_definitions types (Editorial Narratives, Shot Changes, ...) —
        // stored as a plain segments array keyed by the definition id. Some
        // results (e.g. Game Play) were persisted server-side under a
        // different key (e.g. "gameplay" instead of the "plays" definition
        // id), so fall back to the tab's own key(s) before giving up.
        if (seg.definition) {
            const candidateKeys = [seg.definition.id, seg.key, seg.key.replace(/-/g, '')];
            const existing = candidateKeys
                .map((k) => assetRecord?.segments?.[k])
                .find((v) => Array.isArray(v) && v.length > 0);
            if (existing) {
                setTaskState(seg.key, { status: 'ready', segments: existing, fromCache: true });
            }
            // else {
            //     runSegmentAnalysis(seg);
            // }
            return;
        }

        // general + json_schema segments (Detect Criminal Activity) — stored as
        // { [segmentsKey]: [...], overall_summary }, since the segments array can
        // legitimately be empty while overall_summary still has content to show.
        // The asset record can come back with this still JSON-encoded as a string
        // (like the other general-mode caches), so parse it before reading fields
        // — otherwise an already-available result looks empty and re-runs analysis.
        if (seg.general?.segmentsKey) {
            const rawExisting = assetRecord?.segments?.[seg.key];
            const existing = parseMaybeJson(rawExisting);
            const existingIncidents = existing?.[seg.general.segmentsKey];
            const existingSummary = existing?.overall_summary;
            const hasIncidents = Array.isArray(existingIncidents);
            const hasSummary = typeof existingSummary === 'string' && existingSummary.trim();
            if (hasIncidents || hasSummary) {
                setTaskState(seg.key, {
                    status: 'ready',
                    segments: hasIncidents ? normalizeIncidentSegments(existingIncidents) : [],
                    overallSummary: hasSummary ? existingSummary : null,
                    fromCache: true,
                });
                if (existing !== rawExisting) persistSegments(seg.key, existing);
            } 
            // else {
            //    runSegmentAnalysis(seg);
            // }
            return;
        }

        // plain general text/object (Summarize, Hashtags/Topics, Chapter Markers)
        const rawExisting = assetRecord?.segments?.[seg.key];
        const existing = normalizeGeneralCache(rawExisting);
        if ((typeof existing === 'string' && existing.trim()) || (existing && typeof existing === 'object')) {
            setTaskState(seg.key, { status: 'ready', summary: existing, fromCache: true });
            if (existing !== rawExisting) persistSegments(seg.key, existing);
        } 
        // else {
        //     runSegmentAnalysis(seg);
        // }
    }, [activeTab, assetRecord]);

    const userMeta = video?.user_metadata || {};
    const sysMeta = video?.system_metadata || {};
    const genre = flattenValue(parseMaybeJson(userMeta.genre));
    const category = flattenValue(parseMaybeJson(userMeta.carousel));
    const transcript = video?.transcription || video?.transcript || [];
    const activeLabel = RAIL_ITEMS.find((r) => r.key === activeTab)?.label || 'Asset Info';
    const saveAsMissingPrereqs = SAVE_AS_REQUIRED_SEGMENTS.filter(
        (s) => !hasSegmentData(assetRecord?.[s.key])
    );

    // Drives the player-area seekbar: whichever segment tab is selected, if its
    // analysis is ready, replaces Bitmovin's native seekbar with the timeline.
    const activeSegType = SEGMENT_TYPES.find((s) => s.key === activeTab);
    const activeSegTask = activeSegType ? analysisTasks[activeSegType.key] : null;
    const chapterSegments = (activeSegType?.general?.isChapters && activeSegTask?.status === 'ready' && activeSegTask.summary)
        ? normalizeChapterEntries(activeSegTask.summary)
        : null;
    const activeSegments = (activeSegTask?.status === 'ready' && Array.isArray(activeSegTask.segments) && activeSegTask.segments.length > 0)
        ? activeSegTask.segments
        : (chapterSegments && chapterSegments.length > 0 ? chapterSegments : null);
    const activeDefinitionId = activeSegType?.general?.isChapters
        ? 'chapters'
        : (activeSegType?.definition?.id || activeSegType?.general?.segmentsKey);

    return (
        <div style={{ width: '100vw', height: '100vh', background: '#0f0f0f', color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <style>{`
                @media (max-width: 768px) {
                    .tl-body { flex-direction: column !important; overflow-y: auto !important; }
                    .tl-player-wrap { flex: none !important; width: 100% !important; height: 42vh !important; }
                    .tl-rail {
                        width: 100% !important; height: auto !important; flex-direction: row !important;
                        border-left: none !important; border-top: 1px solid rgba(255,255,255,0.08) !important;
                        padding: 8px 12px !important; overflow-x: auto !important; justify-content: flex-start !important;
                    }
                    .tl-rail-exit { margin-top: 0 !important; margin-left: auto !important; }
                    .tl-panel { width: 100% !important; flex: 1 !important; border-left: none !important; border-top: 1px solid rgba(255,255,255,0.08) !important; }
                }
            `}</style>
            {/* Body: player + sidebar */}
            <div className="tl-body" style={{ flex: 1, display: 'flex', minHeight: 0 }}>
                <div className={`tl-player-wrap${activeSegments ? ' tl-hide-native-seekbar' : ''}`} style={{ flex: 1, position: 'relative', background: '#000' }}>
                    <style>{`
                        .tl-hide-native-seekbar .bmpui-ui-seekbar,
                        .tl-hide-native-seekbar .bmpui-ui-playbacktimelabel { display: none !important; }
                    `}</style>
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span className="ai-spinner" />
                        </div>
                    )}
                    {!loading && error && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: 14 }}>
                            {error}
                        </div>
                    )}
                    {!loading && !error && <div ref={playerContainerRef} style={{ position: 'absolute', inset: 0 }} />}

                    {!loading && !error && activeSegments && (
                        <div style={{
                            position: 'absolute', left: 20, right: 20, bottom: 60, zIndex: 30,
                            padding: '10px 14px',
                        }}>
                            <PlayerSegmentSeekbar
                                segments={activeSegments}
                                currentTime={currentTime}
                                duration={sysMeta.duration}
                                onSeek={seekTo}
                                definitionId={activeDefinitionId}
                            />
                        </div>
                    )}
                </div>

                {/* Icon rail */}
                <div className="tl-rail" style={{
                    width: 64, flexShrink: 0, borderLeft: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    padding: '8px 0', background: '#0f0f0f',
                }}>
                    {RAIL_ITEMS.map((item) => {
                        const isActive = !sidebarCollapsed && activeTab === item.key;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                title={item.label}
                                onClick={() => openTab(item.key)}
                                style={{
                                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isActive ? 'rgba(124,58,237,0.25)' : 'transparent',
                                    border: isActive ? '1px solid rgba(124,58,237,0.6)' : '1px solid transparent',
                                    color: isActive ? '#c4b5fd' : 'rgba(255,255,255,0.5)',
                                    cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                                }}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: 24 }}>{item.icon}</span>
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        title="Exit to App"
                        onClick={handleBack}
                        className="tl-rail-exit"
                        style={{
                            width: 44, height: 44, borderRadius: '50%', flexShrink: 0, marginTop: 'auto',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'transparent', border: '1px solid transparent',
                            color: 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                        }}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: 26 }}>exit_to_app</span>
                    </button>
                </div>

                {/* Sidebar panel */}
                {!sidebarCollapsed && (
                    <div className="tl-panel" style={{
                        width: 320, flexShrink: 0, borderLeft: '1px solid rgba(255,255,255,0.08)',
                        display: 'flex', flexDirection: 'column', background: '#16162a',
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '16px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                <span
                                    className="material-symbols-outlined"
                                    style={{
                                        fontSize: 18, flexShrink: 0, width: 32, height: 32, borderRadius: 8,
                                        background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)',
                                        color: '#c4b5fd', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    {RAIL_ITEMS.find((r) => r.key === activeTab)?.icon || 'info'}
                                </span>
                                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {activeLabel}
                                </h4>
                                {/* {activeSegType?.assetType && (
                                    <span style={{
                                        fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                                        color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.15)',
                                        borderRadius: 10, padding: '1px 7px', flexShrink: 0,
                                    }}>
                                        {activeSegType.assetType}
                                    </span>
                                )} */}
                            </div>
                            <button
                                type="button"
                                onClick={() => setSidebarCollapsed(true)}
                                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex' }}
                                aria-label="Collapse panel"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '18px 18px' }}>
                            {activeTab === 'info' && !loading && !error && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                        <span style={{
                                            display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
                                            color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.06)',
                                            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px',
                                        }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>thumb_up</span>0
                                        </span>
                                        <span style={{
                                            display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
                                            color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.06)',
                                            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px',
                                        }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>thumb_down</span>0
                                        </span>
                                        <button
                                            type="button"
                                            onClick={runAllAnalysis}
                                            disabled={analyzeAllStatus === 'running'}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto', padding: '5px 10px',
                                                borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff',
                                                fontSize: 12, fontWeight: 600,
                                                cursor: analyzeAllStatus === 'running' ? 'not-allowed' : 'pointer',
                                                opacity: analyzeAllStatus === 'running' ? 0.6 : 1,
                                            }}
                                        >
                                            {analyzeAllStatus === 'running' && <span className="ai-spinner ai-spinner--sm" />}
                                            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>play_arrow</span>
                                            Analyze All
                                        </button>
                                    </div>
                                    {analyzeAllStatus === 'error' && (
                                        <p style={{ fontSize: 12, color: '#f87171', marginBottom: 12 }}>Could not start analysis for all tasks.</p>
                                    )}

                                    {/* <InfoRow label="Category" value={category} /> */}
                                    <InfoRow label="Title" value={userMeta.assetName} />
                                    <InfoRow label="Availability" value={null} />
                                    <InfoRow label="Created" value={formatDate(video?.created_at)} />
                                    <InfoRow label="Description" value={userMeta.description} />
                                    <InfoRow label="Asset Type" value={userMeta.assetType} />
                                    <InfoRow label="Duration" value={sysMeta.duration ? formatDuration(sysMeta.duration) : null} />
                                    <InfoRow label="Genre" value={genre} />
                                    <InfoRow label="Maturity rating" value={null} />
                                    <InfoRow label="Date of Release" value={null} />
                                    <InfoRow label="Archive Status" value={null} />
                                    <InfoRow label="Storage" value={null} />
                                    <InfoRow label="Tape Name" value={null} />
                                    <InfoRow label="Scene" value={null} />
                                    <InfoRow label="Shot/Take" value={null} />
                                    <InfoRow label="Shot/Date" value={null} />
                                    <InfoRow label="Log Comment" value={null} />
                                    <InfoRow label="Rating" value={null} />
                                    <InfoRow label="Creator" value={null} />
                                    <InfoRow label="Product" value={null} />
                                    <InfoRow label="Event" value={null} />

                                    <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '10px 0' }} />

                                    <InfoRow label="Filename" value={sysMeta.filename} />
                                    <InfoRow label="Resolution" value={sysMeta.width && sysMeta.height ? `${sysMeta.width} × ${sysMeta.height}` : null} />
                                    <InfoRow label="Frame Rate" value={sysMeta.fps ? `${Number(sysMeta.fps).toFixed(2)} fps` : null} />
                                    <InfoRow label="File Size" value={formatBytes(sysMeta.size)} />
                                </>
                            )}

                            {activeTab === 'visual-intelligence' && (
                                <>
                                    {loading && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Loading transcription…</p>}
                                    {!loading && transcript.length === 0 && (
                                        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>No transcription available for this video.</p>
                                    )}
                                    {!loading && transcript.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {transcript.map((line, idx) => {
                                                const isActive = idx === activeTranscriptIndex;
                                                return (
                                                    <div
                                                        key={idx}
                                                        ref={(el) => { transcriptRefs.current[idx] = el; }}
                                                        onClick={() => seekTo(line.start ?? 0)}
                                                        style={{
                                                            display: 'flex', alignItems: 'baseline', gap: 3,
                                                          
                                                        }}
                                                    >
                                                        {/* <span style={{
                                                            fontSize: 9, fontFamily: 'ui-monospace, monospace',
                                                            color: isActive ? '#c4b5fd' : 'rgba(255,255,255,0.3)',
                                                        }}>
                                                            {formatDuration(line.start ?? 0)}
                                                        </span> */}
                                                        <span style={{
                                                            fontSize: 13,
                                                            color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                                                            fontWeight: isActive ? 700 : 400,
                                                        }}>
                                                            {line.text || line.value}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                            {activeTab === 'save-as' && (
                                <>
                                    <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,0.6)' }}>
                                        Export this asset's segments for use in an NLE.
                                    </p>
                                    {saveAsMissingPrereqs.length > 0 ? (
                                        <div style={{
                                            padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)',
                                            border: '1px dashed rgba(255,255,255,0.12)', fontSize: 12, color: 'rgba(255,255,255,0.4)',
                                            textAlign: 'center',
                                        }}>
                                            {saveAsMissingPrereqs.map((s) => s.label).join(', ')} {saveAsMissingPrereqs.length > 1 ? 'are' : 'is'} not prepared yet — run{saveAsMissingPrereqs.length > 1 ? ' those analyses' : ` its analysis`} first.
                                        </div>
                                    ) : saveAsPreparing ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                                            <span className="ai-spinner ai-spinner--sm" />
                                            Preparing exports…
                                        </div>
                                    ) : (
                                        SAVE_AS_TARGETS.map((target) => {
                                            const isAvailable = !!assetRecord?.[target.pathField];
                                            return (
                                            <button
                                                key={target.key}
                                                type="button"
                                                disabled={!isAvailable}
                                                onClick={() => handleSaveAsDownload(target)}
                                                title={isAvailable ? undefined : `${target.label} isn't available for this asset yet.`}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                                    padding: '12px 14px', marginBottom: 10, borderRadius: 10,
                                                    border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.03)',
                                                    color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'left',
                                                    cursor: isAvailable ? 'pointer' : 'not-allowed',
                                                    opacity: isAvailable ? 1 : 0.4,
                                                }}
                                            >
                                                <img src={target.logo} alt="" style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }} />
                                                {target.label}
                                            </button>
                                            );
                                        })
                                    )}
                                    {saveAsMessage && (
                                        <div style={{
                                            marginTop: 10, padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)',
                                            border: '1px dashed rgba(255,255,255,0.12)', fontSize: 12, color: 'rgba(255,255,255,0.4)',
                                            textAlign: 'center',
                                        }}>
                                            {saveAsMessage}
                                        </div>
                                    )}
                                </>
                            )}

                            {SEGMENT_TYPES.filter((seg) => seg.key === activeTab).map((seg) => (
                                <SegmentTypeDetail
                                    key={seg.key}
                                    seg={seg}
                                    task={analysisTasks[seg.key]}
                                    onRun={() => runSegmentAnalysis(seg)}
                                    onSeek={seekTo}
                                    currentTime={currentTime}
                                    videoDuration={sysMeta.duration}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
